import { useState } from 'react';
import { ImageViewer } from '../components/ui/ImageViewer';
import { useParams, useOutletContext } from 'react-router-dom';
import { useTranslations } from '../i18n';
import { theme, styles } from '../lib/theme';
import { SparePartItem } from '../components/cars/SparePartItem';

type SparePartsListProps = {
  carId: string;
  parts: any[];
  loading?: boolean;
  highlightId?: string | null;
  refreshKey?: number;
};

export function SparePartsList({ parts: spareParts, loading, highlightId }: SparePartsListProps) {
  const [viewerImages, setViewerImages] = useState<{ images: string[], index: number, partId: string } | null>(null);

  const { slug } = useParams();
  const outletContext = useOutletContext<any>() || {};
  const { t } = useTranslations();

  const standaloneParts = (spareParts || []).filter((p: any) => !p.expense_id && (!p.log_entry_parts || p.log_entry_parts.length === 0));
  const usedParts = (spareParts || []).filter((p: any) => p.expense_id || (p.log_entry_parts && p.log_entry_parts.length > 0));

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 font-primary">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4" />
        <p className="text-gray-500 font-semibold italic">{t.common.loading}...</p>
      </div>
    );
  }

  return (
    <div className={`${theme.spacing.pagePadding} pb-32 pt-4 relative`}>
      <div className="space-y-6">
        {/* Category: To Buy */}
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1 py-1">
            <h3 className={theme.typography.sectionTitle}>{t.spareParts.toBuy}</h3>
          </div>

          {standaloneParts.length === 0 ? (
            <div className={`${styles.card} p-8 text-center shadow-sm`}>
              <p className="text-gray-400 dark:text-gray-300 text-sm font-medium italic">{t.spareParts.noPartsToBuy}</p>
            </div>
          ) : (
            <div className={`${styles.card} shadow-sm divide-y ${theme.colors.divider}`}>
              {standaloneParts.map((part: any) => (
                <SparePartItem
                  key={part.id}
                  part={part}
                  slug={slug}
                  isAdminView={outletContext.isAdminView}
                  highlighted={highlightId === part.id}
                />
              ))}
            </div>
          )}
        </div>

        {/* Category: Used in Expenses */}
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1 py-1">
            <h3 className={theme.typography.sectionTitle}>{t.spareParts.usedInExpenses}</h3>
          </div>

          {usedParts.length === 0 ? (
            <div className={`${styles.card} p-8 text-center shadow-sm opacity-60`}>
              <p className="text-gray-400 dark:text-gray-300 text-sm font-medium italic">{t.spareParts.noPartsUsed}</p>
            </div>
          ) : (
            <div className={`${styles.card} shadow-sm divide-y ${theme.colors.divider}`}>
              {usedParts.map((part: any) => (
                <SparePartItem
                  key={part.id}
                  part={part}
                  slug={slug}
                  isAdminView={outletContext.isAdminView}
                  highlighted={highlightId === part.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {viewerImages && (
        <ImageViewer
          images={viewerImages.images}
          initialIndex={viewerImages.index}
          onClose={() => setViewerImages(null)}
          relatedLinks={viewerImages.images.map(() => slug ? {
            url: `/${slug}/parts/${viewerImages.partId}`,
            label: (t.spareParts as any).openPart || 'Open Part'
          } : undefined)}
        />
      )}
    </div>
  );
}
